import {
  createFileRoute,
  redirect,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import type { RouterContext } from '@shared/config/router-context';
import { useAbility } from '@features/authorization';
import { useAuth } from '@features/auth';
import { Header } from '@widgets/header';
import { canOpenIntegrationSettings } from './admin-route-access';

/**
 * Admin Section Layout
 *
 * Route: /:locale/admin/*
 * Wraps all admin pages with admin panel layout
 *
 * This file is automatically code-split by TanStack Router.
 */
export const Route = createFileRoute('/$locale/_authenticated/admin')({
  beforeLoad: ({ context, params, location }) => {
    const { ability, auth } = context as RouterContext;

    if (auth.isLoading || !auth.isAuthenticated) {
      return;
    }

    if (
      !ability.can('manage', 'all') &&
      !canOpenIntegrationSettings(location.pathname, params.locale, auth.permissions)
    ) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/$locale/unauthorized',
        params: { locale: params.locale },
      });
    }
  },
  component: AdminLayout,
});

function AdminLayout() {
  const { locale } = Route.useParams();
  const ability = useAbility();
  const auth = useAuth();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });

  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated) {
      return;
    }

    if (
      !ability.can('manage', 'all') &&
      !canOpenIntegrationSettings(pathname, locale, auth.permissions)
    ) {
      void navigate({
        to: '/$locale/unauthorized',
        params: { locale },
        replace: true,
      });
    }
  }, [ability, auth.isAuthenticated, auth.isLoading, auth.permissions, locale, navigate, pathname]);

  return (
    <div className="flex h-screen min-h-0 flex-col pt-16 min-[992px]:pt-0">
      <Header locale={locale} className="min-[992px]:hidden" />

      <main className="min-h-0 min-w-0 flex-1 overflow-y-auto p-6 [scrollbar-gutter:stable_both-edges]">
        <Outlet />
      </main>
    </div>
  );
}
